/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { FiChevronLeft } from 'react-icons/fi';
import AppAlert from '../../../../shared/components/app-alert/AppAlert';
import AppLoading from '../../../../shared/components/app-loading/AppLoading';
import { CRUISE_STEPS } from '../../constants/cruiseConstants';
import { flowUpTo, resultsUrl, useCruiseFlow } from '../../utils/cruiseFlow';
import '../../styles/cruise.scss';

// Which flow slices each earlier step needs to open again.
const STEP_NEEDS = { details: 'fare', cabin: 'category' };

/** Step dots across the top; steps already done are links back. */
export function CruiseProgress({ current }) {
  const navigate = useNavigate();
  const flow = useCruiseFlow();
  const index = CRUISE_STEPS.findIndex((s) => s.key === current);
  const goTo = (step) => {
    if (step.key === 'results' && flow.search) {
      navigate(resultsUrl(flow.search));
    } else if (STEP_NEEDS[step.key]) {
      navigate(step.route, { state: flowUpTo(flow, STEP_NEEDS[step.key]) });
    } else {
      navigate(step.route);
    }
  };

  return (
    <div className="cr-progress">
      <div className="cr-progress__inner">
        {CRUISE_STEPS.map((step, k) => {
          let state = 'todo';
          if (k < index) state = 'done';
          else if (k === index) state = 'current';
          // After booking there is nothing to go back to.
          const clickable = state === 'done' && current !== 'done';
          const label = (
            <>
              <span className="cr-progress__dot">{k + 1}</span>
              <span className="cr-progress__label">{step.label}</span>
            </>
          );
          return (
            <div key={step.key} className={`cr-progress__step is-${state}`}>
              {clickable ? (
                <button
                  type="button"
                  className="cr-progress__link"
                  onClick={() => goTo(step)}
                >
                  {label}
                </button>
              ) : (
                label
              )}
              {k < CRUISE_STEPS.length - 1 && (
                <span className="cr-progress__bar" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

CruiseProgress.propTypes = {
  current: PropTypes.string.isRequired,
};

export function CruiseBackLink({ label, onClick }) {
  return (
    <button type="button" className="cr-back" onClick={onClick}>
      <FiChevronLeft size={16} />
      {label}
    </button>
  );
}

CruiseBackLink.propTypes = {
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

/** Page shell: progress bar, centred container, loader and alert. */
function CruiseLayout({
  step,
  loading,
  alert,
  onAlertHide,
  narrow,
  header,
  children,
}) {
  return (
    <div className="cruise-module cr-page">
      <CruiseProgress current={step} />
      {header}
      <div className={`cr-container ${narrow ? 'cr-container--narrow' : ''}`}>
        {children}
      </div>
      <AppLoading isLoading={Boolean(loading)} />
      {alert && (
        <AppAlert
          showIcon
          variant={alert.variant}
          title={alert.title}
          description={alert.description}
          onHide={onAlertHide}
        />
      )}
    </div>
  );
}

CruiseLayout.propTypes = {
  step: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  alert: PropTypes.object,
  onAlertHide: PropTypes.func,
  narrow: PropTypes.bool,
  header: PropTypes.node,
  children: PropTypes.node,
};

CruiseLayout.defaultProps = {
  loading: false,
  alert: null,
  onAlertHide: () => {},
  narrow: false,
  header: null,
  children: null,
};

export default CruiseLayout;
